/**
 * QR-Sticker-PDF-Renderer (P-6 Enhancement).
 *
 * Standard: Avery L7160 / L7161 / generisches 3×7-A4-Etikett
 *   Etikett: 63,5 × 38,1 mm
 *   Rand links/oben: 7,1 / 15,1 mm
 *   Abstand horizontal: 2,5 mm
 *   Abstand vertikal: 0 mm
 *   21 Etiketten pro Bogen (3 Spalten × 7 Zeilen)
 *
 * Keine Server-Roundtrips -- QR-Code + PDF entstehen im Browser.
 */

import { jsPDF } from "jspdf";
import QRCode from "qrcode";
import { qrPayload } from "./qrPayload";

export { qrPayload } from "./qrPayload";

// Label layout Avery L7160 (21 per A4 sheet)
const PAGE_W = 210; // mm
const PAGE_H = 297;
const LABEL_W = 63.5;
const LABEL_H = 38.1;
const MARGIN_L = 7.1;
const MARGIN_T = 15.1;
const GAP_X = 2.5;
const GAP_Y = 0;
const COLS = 3;
const ROWS = 7;
const PER_PAGE = COLS * ROWS;

function labelOrigin(index) {
    const slot = index % PER_PAGE;
    const col = slot % COLS;
    const row = Math.floor(slot / COLS);
    const x = MARGIN_L + col * (LABEL_W + GAP_X);
    const y = MARGIN_T + row * (LABEL_H + GAP_Y);
    return { x, y };
}

/**
 * Build QR payload for a given entity type + id.
 * Convention: `platzwart://<type>/<id>` -- die Mobile ScanBox kann
 * diese URLs später direkt parsen.
 */
async function drawLabel(pdf, { x, y, payload, title, subtitle }) {
    // QR code
    const qrSize = LABEL_H - 6; // square, vertikal limitierend
    const qrDataUrl = await QRCode.toDataURL(payload, {
        errorCorrectionLevel: "M",
        margin: 0,
        scale: 6,
    });
    pdf.addImage(qrDataUrl, "PNG", x + 2, y + 3, qrSize, qrSize);

    // Text
    const textX = x + qrSize + 4;
    const textW = LABEL_W - qrSize - 6;
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(11);
    const titleLines = pdf.splitTextToSize(title || "", textW);
    // Ohne Untertitel darf der Klarname mehr Zeilen belegen (Fach-Etikett).
    pdf.text(titleLines.slice(0, subtitle ? 2 : 4), textX, y + 8);

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(7);
    if (subtitle) {
        const subLines = pdf.splitTextToSize(subtitle, textW);
        pdf.text(subLines.slice(0, 3), textX, y + 20);
    }

    // Keine Payload-Fußzeile mehr (Wunsch Hauptnutzer 2026-08-23, #114):
    // die platzwart://-Adresse steckt im QR-Code, auf dem Etikett stört sie.
}

/**
 * renderLabels(items) -> downloads PDF.
 *
 * items: [{ kind: 'compartment'|'article'|'asset', id, title, subtitle }]
 */
export async function renderLabels(items, filename = "platzwart-qr-sticker.pdf") {
    if (!items || items.length === 0) throw new Error("Keine Etiketten gewählt.");

    const pdf = new jsPDF({ unit: "mm", format: "a4" });

    for (let i = 0; i < items.length; i++) {
        if (i > 0 && i % PER_PAGE === 0) pdf.addPage();
        const it = items[i];
        const { x, y } = labelOrigin(i);
        const payload = qrPayload(it.kind, it.id);
        await drawLabel(pdf, {
            x,
            y,
            payload,
            title: it.title,
            subtitle: it.subtitle,
        });
    }
    pdf.save(filename);
}

/** Zusatz: freie Positions-Zählung (wenn der Bogen schon teils bedruckt ist). */
export async function renderLabelsWithOffset(items, startSlot = 0, filename) {
    const padded = Array(startSlot).fill(null).concat(items);
    const pdf = new jsPDF({ unit: "mm", format: "a4" });
    for (let i = 0; i < padded.length; i++) {
        if (i > 0 && i % PER_PAGE === 0) pdf.addPage();
        const it = padded[i];
        if (!it) continue;
        const { x, y } = labelOrigin(i);
        const payload = qrPayload(it.kind, it.id);
        await drawLabel(pdf, {
            x,
            y,
            payload,
            title: it.title,
            subtitle: it.subtitle,
        });
    }
    pdf.save(filename || "platzwart-qr-sticker.pdf");
}

export const LABEL_LAYOUT = {
    format: "Avery L7160",
    label: `${LABEL_W} × ${LABEL_H} mm`,
    perPage: PER_PAGE,
    cols: COLS,
    rows: ROWS,
};
